// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * cm-coding: Administrators must understand that schemas define item fields,
 * knowledge bases supply guidance, and datasets connect that guidance to
 * analysis. Show the same three-step map and current location on all three
 * pages so users can enter the setup flow anywhere without treating the pages
 * as unrelated features. Keep this shared copy in plain language because it is
 * the product explanation of that workflow.
 */
import type { FC, ReactNode } from 'react'
import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'

export type ConfigStep = 'schemas' | 'knowledge-bases' | 'datasets'

const STEPS: { id: ConfigStep; path: string; title: string; desc: string }[] = [
  {
    id: 'schemas',
    path: '/schemas',
    title: 'Schemas',
    desc: 'Define the fields the AI fills in when it reviews a document.',
  },
  {
    id: 'knowledge-bases',
    path: '/knowledge-bases',
    title: 'Knowledge Bases',
    desc: 'Optional guidance and criteria the AI consults while reviewing.',
  },
  {
    id: 'datasets',
    path: '/datasets',
    title: 'Datasets',
    desc: 'Give an agent a schema to fill and a knowledge base to consult.',
  },
]

interface ConfigFlowStripProps {
  current: ConfigStep
}

export const ConfigFlowStrip: FC<ConfigFlowStripProps> = ({ current }) => (
  <div
    className="rounded-xl border border-[#e7ecf2] bg-white px-5 py-4 shadow-[0_1px_2px_rgba(2,8,23,0.04)]"
    data-testid="config-flow"
  >
    <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">
      How review setup fits together
    </div>
    <div className="mt-2.5 flex flex-col gap-2 lg:flex-row lg:items-stretch lg:gap-3">
      {STEPS.map((step, i) => {
        const isCurrent = step.id === current
        const body: ReactNode = (
          <>
            <div className="flex items-center gap-2">
              <span
                className={`flex h-[18px] w-[18px] items-center justify-center rounded-full text-[10.5px] font-semibold ${
                  isCurrent ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'
                }`}
              >
                {i + 1}
              </span>
              <span className="text-[13px] font-[600] text-slate-900">{step.title}</span>
              {isCurrent && (
                <span className="rounded-full bg-slate-900 px-2 py-[2px] text-[10px] font-semibold text-white">
                  You are here
                </span>
              )}
            </div>
            <p className="mt-1 text-[12px] leading-snug text-slate-500">{step.desc}</p>
          </>
        )
        return (
          <Fragment key={step.id}>
            {i > 0 && (
              <ArrowRight className="hidden h-4 w-4 shrink-0 self-center text-slate-300 lg:block" />
            )}
            {isCurrent ? (
              <div
                className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5"
                data-testid={`config-flow-${step.id}`}
                aria-current="step"
              >
                {body}
              </div>
            ) : (
              <Link
                to={step.path}
                data-testid={`config-flow-${step.id}`}
                className="flex-1 rounded-lg border border-transparent px-3.5 py-2.5 transition-colors duration-[120ms] hover:border-slate-200 hover:bg-slate-50"
              >
                {body}
              </Link>
            )}
          </Fragment>
        )
      })}
    </div>
    <p className="mt-2.5 text-[12px] text-slate-400">
      Pipeline runs fill datasets automatically — results appear on the document review screens.
    </p>
  </div>
)
